import { moveEntry } from '@portals/shared/common/utils/arrayUtils';
import { useCallback, useEffect, useState } from 'react';

import { mapNavEntriesToNavNodesWithPath } from '../utils/navHistory';
import { clearEntries, loadEntries, saveEntries, upsertEntry } from '../utils/navHistoryStorage';
import { buildTreeFromEntries } from '../utils/navTreeBuilder';
import type { NavEntry, NavNode, NavNodeType, NavNodeWithPath, ParentInfo, UseNavHistoryOptions } from '../utils/types';
import { useNavHistoryConfig } from './NavHistoryConfigContext';

interface UseNavHistoryReturn {
  /** Get pinned and recent pages with breadcrumb trails */
  getPinnedAndRecentPages: (limit?: number) => NavNodeWithPath[];
  /** Build hierarchical tree from recent entries (for tree components) */
  buildTree: () => NavNode;
  /** Clear all navigation history */
  clearHistory: () => void;
  /** Manually track a page visit */
  trackPage: (path: string, name: string, type: NavNodeType, parents?: ParentInfo[]) => void;
  /** Adds a pin to the page (if not already pinned) and moves it to the newIndex */
  pinPage: (path: string, newIndex: number) => void;
  /** Removes the pin from the page and moves it to the most recently visited page in the list */
  unpinPage: (path: string) => void;
  /** Removes the page from the pinned and recent pages */
  removePage: (path: string) => void;
}

/**
 * Helper to track page visit and persist in storage
 */
function trackPageVisit(
  path: string,
  name: string,
  type: NavNodeType,
  maxEntries: number,
  storageKey: string,
  parents?: ParentInfo[],
): void {
  const curr = loadEntries(storageKey);
  let updated = upsertEntry(curr, path, name, type, parents);
  updated = updated.slice(0, maxEntries);
  saveEntries(storageKey, updated);
}

/**
 * Helper to update pin state and persist in storage
 */
function pinEntryInStorage(path: string, newIndex: number, maxPinned: number, storageKey: string): void {
  const entries = loadEntries(storageKey);
  const currentEntryIndex = entries.findIndex((e) => e.path === path);
  const currentEntry = entries[currentEntryIndex];
  if (!currentEntry) return;

  if (!currentEntry.pinned) {
    const pinnedCount = entries.filter((e) => e.pinned).length;
    if (pinnedCount >= maxPinned) return;
    currentEntry.pinned = true;
  }
  moveEntry(entries, currentEntryIndex, newIndex);
  saveEntries(storageKey, entries);
}

/**
 * Helper to update pin state and persist in storage
 */
function unpinEntryInStorage(path: string, storageKey: string): void {
  const entries = loadEntries(storageKey);
  const currentEntryIndex = entries.findIndex((e) => e.path === path);
  const currentEntry = entries[currentEntryIndex];
  if (!currentEntry || !currentEntry.pinned) return;

  const firstUnpinnedIndex = entries.findIndex((e) => !e.pinned);
  if (firstUnpinnedIndex === -1) {
    moveEntry(entries, currentEntryIndex, entries.length - 1);
  } else {
    moveEntry(entries, currentEntryIndex, firstUnpinnedIndex - 1);
  }

  currentEntry.pinned = false;
  saveEntries(storageKey, entries);
}

/**
 * Helper to delete entry and persist in storage
 */
function deleteEntryFromStorage(path: string, storageKey: string): void {
  const curr = loadEntries(storageKey);
  const updated = curr.filter((e) => e.path !== path);
  saveEntries(storageKey, updated);
}

/**
 * Syncs entries state with storage updates
 */
function useSyncEntries(storageKey: string, setEntries: (entries: NavEntry[]) => void): void {
  useEffect(() => {
    // Initial load
    setEntries(loadEntries(storageKey));

    // Sync handler for updates
    const syncEntries = (key?: string) => {
      if (!key || key === storageKey) {
        setEntries(loadEntries(storageKey));
      }
    };

    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ storageKey: string }>;
      syncEntries(customEvent.detail?.storageKey);
    };

    const handleStorage = (e: StorageEvent) => syncEntries(e.key ?? undefined);

    globalThis.addEventListener('navHistoryUpdate', handleUpdate);
    globalThis.addEventListener('storage', handleStorage);

    return () => {
      globalThis.removeEventListener('navHistoryUpdate', handleUpdate);
      globalThis.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);
}

/**
 * Hook for tracking navigation history across the application
 *
 * Call `trackPage(path, name, type, parents?)` to manually track a page visit.
 * Use `getRecentPages(limit?)` to retrieve history with breadcrumbs.
 * Use `buildTree()` to build hierarchical tree structure for nested views.
 *
 * @param options Configuration options
 * @param options.storageKey - Override storage key (defaults to context value)
 * @param options.maxEntries - Maximum entries to store (default: 50)
 *
 * @example
 * ```tsx
 * // Recommended: Use with NavHistoryConfigProvider for automatic storage key
 * // App.tsx wraps app with provider
 * function MyComponent() {
 *   const { trackPage, getRecentPages } = useNavHistory();
 *   // storageKey comes from context automatically
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Alternative: Provide storageKey explicitly for standalone usage
 * function MyComponent() {
 *   const { trackPage, getRecentPages } = useNavHistory({
 *     storageKey: 'my_custom_history',
 *     maxEntries: 20
 *   });
 * }
 * ```
 */
export function useNavHistory(options?: UseNavHistoryOptions): UseNavHistoryReturn {
  const { storageKey: optionStorageKey, maxEntries: optionMaxEntries } = options ?? {};
  const { storageKey: contextStorageKey, maxEntries: contextMaxEntries, maxPinned } = useNavHistoryConfig();
  const storageKey = optionStorageKey ?? contextStorageKey;
  const maxEntries = optionMaxEntries ?? contextMaxEntries;

  // Use state instead of ref to trigger re-renders on changes
  const [entries, setEntries] = useState<NavEntry[]>(loadEntries(storageKey));

  // Sync entries state with storage updates
  useSyncEntries(storageKey, setEntries);

  const trackPage = useCallback(
    (path: string, name: string, type: NavNodeType, parents?: ParentInfo[]): void => {
      trackPageVisit(path, name, type, maxEntries, storageKey, parents);
    },
    [storageKey, maxEntries],
  );

  const getPinnedAndRecentPages = useCallback(
    (limit: number = maxEntries): NavNodeWithPath[] => mapNavEntriesToNavNodesWithPath(entries, limit),
    [entries, maxEntries],
  );

  const buildTree = useCallback((): NavNode => buildTreeFromEntries(entries), [entries]);

  const clearHistory = useCallback((): void => {
    clearEntries(storageKey);
  }, [storageKey]);

  const pinPage = useCallback(
    (path: string, newIndex: number) => {
      pinEntryInStorage(path, newIndex, maxPinned, storageKey);
    },
    [storageKey, maxPinned],
  );

  const unpinPage = useCallback(
    (path: string) => {
      unpinEntryInStorage(path, storageKey);
    },
    [storageKey],
  );

  const removePage = useCallback(
    (path: string) => {
      deleteEntryFromStorage(path, storageKey);
    },
    [storageKey],
  );

  return {
    getPinnedAndRecentPages,
    buildTree,
    clearHistory,
    trackPage,
    pinPage,
    unpinPage,
    removePage,
  };
}
